import { bindable, inject } from "aurelia";
import { I18N } from "@aurelia/i18n";
import { IRouter } from '@aurelia/router';
import { toastifyError } from '@helpers/toastify';
import { newInstanceForScope } from '@aurelia/kernel';
import { IValidationRules } from '@aurelia/validation';
import { IValidationController } from '@aurelia/validation-html';
import { IAuthService } from '@interfaces/auth-service/auth-service-interface';
import { Parameters } from "@aurelia/router";
import { GoogleAnalyticsHelper } from "@helpers/google-analytics/google-analytics-helper";

@inject(I18N, IRouter, IAuthService, IValidationRules, newInstanceForScope(IValidationController))
export class LoginOtpPage {

  @bindable length: number = 6;
  @bindable value: string = '';

  private assets = [
    'assets/images/login/homem_magica_post.webp',
    'assets/images/login/advogado.webp',
  ]

  private hasSendEmail: boolean = false
  private isCodeComplete: boolean = false
  private hasCodeError: boolean = false

  private inputs: HTMLInputElement[] = [];

  private intervalId: NodeJS.Timeout;

  private timeLeft: number;
  private initialTime: number = 300;
  private countdown: string | null = '05:00';

  private email: string;
  private termOfAdhesion: boolean = false;
  private isLoading: boolean;

  private mq: MediaQueryList;

  constructor(
    private I18N: I18N,
    private router: IRouter,
    private auth: IAuthService,
    private validationRules: IValidationRules,
    private validationController: IValidationController
  ) {
    validationRules
      .on(this)
      .ensure('email')
      .email()
      .withMessage('Este não é um e-mail válido.')
  }

  loading(parameters: Parameters) {
    if (parameters) {
      localStorage.setItem('cm_metadata', JSON.stringify(parameters));
    }

    this.mq = window.matchMedia('(prefers-color-scheme: dark)');

    this.syncTheme();

    this.mq.addEventListener('change', this.syncTheme.bind(this));
  }

  syncTheme() {

    const isDark = this.mq.matches

    localStorage.setItem('rankmyseite-color-theme', isDark ? 'dark' : 'light');

    document.body.classList.toggle('dark', isDark);
  }

  detaching() {
    this.mq.removeEventListener('change', this.syncTheme);
  }


  get randomAsset() {
    return `background-image: url('${this.assets[Math.floor(Math.random() * this.assets.length)]}')`;
  }

  private async signInOTP() {

    this.validationRules.ensure('email').required()
      .withMessage('Email é obrigatório')

    const result = await this.validationController.validate();

    if (!result.valid) return;

    this.isLoading = true;

    try {

      const cm_metadata = localStorage.getItem('cm_metadata') ?? '{}';
      GoogleAnalyticsHelper.reportConversion();


      const response = await this.auth.signInOTP({ email: this.email, metadata: JSON.parse(cm_metadata) });

      if (!response) throw new Error('Algo deu errado, tente novamente mais tarde');

      localStorage.removeItem('cm_metadata');

      this.hasSendEmail = true;
      this.startCountdown();

      this.inputs = Array.from(document.querySelectorAll('.otp-input'));
    } catch (error) {
      if (error instanceof Error) {
        toastifyError({ message: error.message });
      } else {
        toastifyError({ message: 'Algo deu errado, tente novamente mais tarde' });
      }
    } finally {
      this.isLoading = false;
    }
  }

  private async handleLoginWithGoogle() {
    try {
      const cm_metadata = localStorage.getItem('cm_metadata');
      GoogleAnalyticsHelper.reportConversion();

      await this.auth.signInForGoogle({
        ...cm_metadata && { metadata: JSON.parse(cm_metadata) }
      });

      localStorage.removeItem('cm_metadata');

      this.router.load('/');
    } catch (error) {
      if (error instanceof Error) {
        toastifyError({ message: error.message })
      } else {
        toastifyError({ message: 'Algo deu errado, tente novamente mais tarde' });
      }
    }
  }

  handleInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;

    const value = input.value

    if (value === '') this.movePrevious(index);

    // verificar se é numero se não for apagar do input
    if (!/^[0-9]$/.test(value)) {
      input.value = '';
      this.isCodeComplete = false
      return
    }

    if (value) {
      input.value = value; // Garante que o número apareça no campo
      this.moveNext(index);
    } else {
      this.movePrevious(index);
    }

    this.updateValue();

    if (this.value.length === 6) {
      this.isCodeComplete = true
    }
  }

  pasteCode(event: ClipboardEvent) {
    event.preventDefault();

    const pasteData = event.clipboardData?.getData('text') || '';
    const numbersOnly = pasteData.replace(/\D/g, ''); // Remove caracteres não numéricos

    if (!numbersOnly) return;

    for (let i = 0; i < this.length; i++) {
      if (numbersOnly.at(i)) { // Fixed syntax error with .at() method
        this.inputs.at(i).value = numbersOnly.at(i); // Atualiza o input com os números colados
      } else {
        this.inputs.at(i).value = ''; // Limpa os campos restantes
      }
    }

    this.updateValue(); // Atualiza o valor total do OTP
    this.inputs?.at(this.length - 1)?.focus(); // Move o foco para o último campo
    if (numbersOnly.length === this.length) {
      this.isCodeComplete = true
    }
  }

  movePrevious(index: number) {
    if (index > 0) {
      this.inputs?.at(index - 1)?.focus(); // Move o foco para o input anterior
    }
  }

  moveNext(index: number) {
    if (index < this.length - 1) {
      this.inputs?.at(index + 1)?.focus();
    }
  }

  updateValue() {
    this.value = this.inputs.map(input => input.value).join('');
  }

  startCountdown() {
    this.timeLeft = this.initialTime;

    this.intervalId = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
        this.updateCountdownDisplay();
      } else {
        clearInterval(this.intervalId);
        this.countdown = null;
      }
    }, 1000);
  }

  updateCountdownDisplay() {
    const minutes = Math.floor(this.timeLeft / 60);
    const seconds = this.timeLeft % 60;

    this.countdown = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  private async resendOTP() {
    try {
      const response = await this.auth.signInOTP({ email: this.email });

      if (!response) throw new Error('Algo deu errado, tente novamente mais tarde');

      this.startCountdown();
    } catch (error) {
      if (error instanceof Error) {
        toastifyError({ message: error.message });
      } else {
        toastifyError({ message: 'Algo deu errado, tente novamente mais tarde' });
      }
    }
  }

  private async signInWithOTP() {
    this.isLoading = true;

    try {
      const response = await this.auth.verifyEmailOTP({ email: this.email, otp: this.value });

      if (!response) {
        this.hasCodeError = true
        throw new Error('Algo deu errado, tente novamente mais tarde');
      }

      this.router.load('/');

      this.email = ''
      this.value = ''
      this.hasCodeError = false
    } catch (error) {
      if (error instanceof Error) {
        toastifyError({ message: error.message });
      } else {
        toastifyError({ message: 'Algo deu errado, tente novamente mais tarde' });
      }
    } finally {
      this.isLoading = false;
    }
  }

  private changeEmail() {
    this.hasSendEmail = false;
    this.email = ''
    this.value = ''
    this.hasCodeError = false
  }
}
